import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, MapPin, Shield, ShieldCheck, Phone, Mail, Car, ArrowLeft, Calendar, Route, CheckCircle } from 'lucide-react';
import axios from 'axios';
import './PublicProfile.css';

import { API as API_URL } from '../utils/apiConfig';

export const PublicProfile = () => {
  const { userId } = useParams();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchReviews();
    }
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/${userId}/reviews`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      // Reviews optional
    }
  };

  const timeAgo = (dateString) => {
    if (!dateString) return '';
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    if (seconds < 86400) return t('notifications.justNow');
    const days = Math.floor(seconds / 86400);
    if (days < 30) return `${days}d`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo`;
    return `${Math.floor(months / 12)}y`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
        <p style={{ color: '#94a3b8', fontSize: '1.1rem' }}>{t('publicProfile.notFound')}</p>
        <Link to="/" style={{ color: '#2563eb', fontWeight: 600 }}>{t('common.back')}</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="public-profile-page">
      <Link to="/search" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#2563eb', textDecoration: 'none', fontWeight: 600, marginBottom: '1.5rem' }}>
        <ArrowLeft size={18} /> {t('common.back')}
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }} className="public-profile-grid">
        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Photo & Name */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '2rem', textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <div style={{
              width: '96px', height: '96px', borderRadius: '50%', margin: '0 auto 1rem',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: '2rem', fontWeight: 700, overflow: 'hidden'
            }}>
              {profile.profilePhoto ? (
                <img src={profile.profilePhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (profile.firstName?.[0] || '?').toUpperCase()
              )}
            </div>

            <h1 style={{ fontSize: '1.3rem', fontWeight: 800, margin: '0 0 0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {profile.firstName} {profile.lastName?.[0] ? `${profile.lastName[0]}.` : ''}
              {profile.verified?.id && (
                <span data-testid="profile-verified-badge" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                  padding: '0.15rem 0.5rem', borderRadius: '20px', fontSize: '0.7rem',
                  fontWeight: 700, background: '#dcfce7', color: '#15803d', border: '1px solid #86efac',
                }}>
                  <ShieldCheck size={14} /> {t('verification.badge')}
                </span>
              )}
            </h1>

            {profile.city && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', color: '#64748b', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                <MapPin size={14} /> {profile.city}
              </div>
            )}

            {/* Rating */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Star size={18} fill="#fbbf24" stroke="#fbbf24" />
              <strong style={{ fontSize: '1.1rem' }}>{profile.rating ? Number(profile.rating).toFixed(1) : '-'}</strong>
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>({profile.reviewCount || 0} {t('publicProfile.reviews')})</span>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>{profile.totalRides || 0}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{t('publicProfile.rides')}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>{profile.reviewCount || 0}</div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{t('publicProfile.reviews')}</div>
              </div>
            </div>
          </div>

          {/* Verification */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.75rem' }}>{t('publicProfile.verification')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {profile.verified?.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.85rem' }}>
                  <CheckCircle size={16} /> {t('publicProfile.phoneVerified')}
                </div>
              )}
              {profile.verified?.id && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.85rem' }}>
                  <Shield size={16} /> {t('publicProfile.idVerified')}
                </div>
              )}
              {profile.verified?.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.85rem' }}>
                  <Mail size={16} /> {t('publicProfile.emailVerified')}
                </div>
              )}
            </div>
          </div>

          {/* Member Since */}
          {profile.memberSince && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8', fontSize: '0.85rem', padding: '0 0.5rem' }}>
              <Calendar size={14} /> {t('publicProfile.memberSince')} {profile.memberSince}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Bio */}
          {profile.bio && (
            <div style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #e2e8f0' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem' }}>{t('publicProfile.about')} {profile.firstName}</h2>
              <p style={{ color: '#475569', lineHeight: 1.7, margin: 0 }}>{profile.bio}</p>
            </div>
          )}

          {/* Vehicle */}
          {profile.vehicle && (
            <div style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #e2e8f0' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                <Car size={20} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
                {t('publicProfile.vehicle')}
              </h2>
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem' }}>
                <h3 style={{ margin: '0 0 0.25rem', fontWeight: 700, fontSize: '1rem' }}>
                  {profile.vehicle.make} {profile.vehicle.model}
                </h3>
                <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                  {profile.vehicle.color} {profile.vehicle.year ? `(${profile.vehicle.year})` : ''} {profile.vehicle.licensePlate ? ` · ${profile.vehicle.licensePlate}` : ''}
                </p>
              </div>
            </div>
          )}

          {/* Reviews */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #e2e8f0' }}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '1rem' }}>
              {t('publicProfile.reviewsTitle')} ({reviews.length})
            </h2>

            {reviews.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {reviews.map((review, i) => (
                  <div key={i} style={{ background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '32px', height: '32px', borderRadius: '50%', background: '#e2e8f0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 600
                        }}>
                          {review.reviewer.photo ? (
                            <img src={review.reviewer.photo} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            review.reviewer.name[0]
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{review.reviewer.name}</div>
                          {review.route?.from && (
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              <Route size={10} style={{ display: 'inline', marginRight: '0.25rem' }} />
                              {review.route.from} → {review.route.to}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} size={14} fill={j < review.rating ? '#fbbf24' : 'none'} stroke={j < review.rating ? '#fbbf24' : '#d1d5db'} />
                        ))}
                      </div>
                    </div>
                    {review.text && (
                      <p style={{ color: '#475569', margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>{review.text}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                <Star size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <p>{t('publicProfile.noReviews')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
